import { GoogleMap } from '@capacitor/google-maps';
import { PreserveStringAlias } from 'src/app/utils/alias.utils';
import { MarkerConfig } from './marker.config';
import { Marker as GoogleMapsMarker } from '@capacitor/google-maps';
import { DeferredPromise } from 'src/app/utils/promise/deferred-promise.util';
import { Size as GoogleMapsSize } from '@capacitor/google-maps/dist/typings/definitions';

type MarkerId = string & PreserveStringAlias;
type IconSize = {
  width: number;
  height: number;
};
export class Marker {
  private config: MarkerConfig;
  private markerId: MarkerId | null = null;

  private constructor(private map: GoogleMap) {}

  static async create(map: GoogleMap, config: MarkerConfig): Promise<Marker> {
    const marker = new Marker(map);
    await marker.add(config);

    return marker;
  }

  get id(): MarkerId | null {
    return this.markerId;
  }

  async update(updateOpts: Partial<MarkerConfig>): Promise<void> {
    if (!this.markerId) {
      return;
    }

    const opts = { ...this.config, ...updateOpts };

    await this.remove();
    await this.add(opts);
  }

  async remove(): Promise<void> {
    if (!this.markerId) {
      return;
    }

    await this.map.removeMarker(this.markerId);
    this.markerId = null;
  }

  private async add(config: MarkerConfig): Promise<void> {
    const markerConfig: GoogleMapsMarker = await this.convertMarkerConfigToGoogleMapsMarker(config);
    const id = await this.map.addMarker(markerConfig);

    this.config = config;
    this.markerId = id;
  }

  private isVisibleToOpacity(isVisible: boolean | null): number {
    if (isVisible == null) {
      return 1;
    }
    return isVisible ? 1 : 0;
  }

  private async convertMarkerConfigToGoogleMapsMarker(config: MarkerConfig): Promise<GoogleMapsMarker> {
    //Google maps scales the marker icon. Set IconSize to the actual size of the icon.
    const size = config.iconUrl ? await this.getGoogleMapsIconSize(config.iconUrl) : null;
    return {
      coordinate: config.coordinate,
      title: config.title,
      snippet: config.snippet,
      iconUrl: config.iconUrl,
      opacity: this.isVisibleToOpacity(config.visible),
      zIndex: config.zIndex,
      iconSize: size
    };
  }

  private async getGoogleMapsIconSize(iconUrl: string): Promise<GoogleMapsSize> {
    const deferredPromise = new DeferredPromise<IconSize>();

    const img = new Image();
    img.onload = () => {
      const iconSize: GoogleMapsSize = {
        width: img.width,
        height: img.height
      };
      deferredPromise.resolve(iconSize);
    };
    img.src = iconUrl;

    const size = await deferredPromise;
    return size;
  }
}
